import { useEffect, useMemo, useState } from "react";
import { useI18n } from "i18n";
import { CommonAuthContainer } from "modules/auth/common/CommonAuthContainer";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { Events, PAGES, usePageTracking } from "modules/tracking";
import { useHistory, useLocation } from "react-router";
import { useGetAppsumoLicenseKey } from "modules/appsumo/hooks/useGetAppsumoLicenseKey";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import useAuth from "modules/auth/hooks/useAuth";
import { useSelector } from "react-redux";
import { selectBrandName } from "modules/settings/redux/selectors";
import { getRemoHosts } from "config";
import { SignUpViewTrial } from "../SignUpViewTrial";
import SignUpView from "../SignUpView";
import { styles } from "./styles";

export const SignUpContainer = () => {
  const { t } = useI18n(["common", "auth"]);
  const [prefilledEmail, setPrefilledEmail] = useState("");
  const { code } = useQueryParams();
  const { signOut, isAuthenticated } = useAuth();
  const { redirectTo } = useQueryParams();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const isRemoDomain = getRemoHosts().includes(window.location.host);
  const brandName = useSelector(selectBrandName);
  const { getAppsumoLicenseKey, isGetAppsumoLicenseKeyLoading } =
    useGetAppsumoLicenseKey(code as string);

  const isTrial = pathname.includes("start-free-trial");

  useEffect(() => {
    if (isTrial) signOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isTrial || !isAuthenticated) return;

    push(redirectTo ? redirectTo.toString() : "/my-events");
  }, [isAuthenticated, isTrial, push, redirectTo]);

  useEffect(() => {
    if (code && pathname.includes("appsumo")) {
      getAppsumoLicenseKey();
    }
  }, [getAppsumoLicenseKey, code, pathname]);

  usePageTracking(PAGES.SIGNUP);
  if (isGetAppsumoLicenseKeyLoading) {
    <PageLoader message={t("common:loading")} />;
  }

  const title = useMemo(() => {
    if (isTrial) return t("auth:signup.free.trial");

    if (isRemoDomain) return t("auth:sign.up.events.virtual");

    return t("auth:sign.up.custom.domain", {
      companyName: brandName,
    });
  }, [brandName, isRemoDomain, isTrial, t]);

  return (
    <CommonAuthContainer
      authType={"signup" as const}
      trackEvent={Events.SIGNUP_VIEWED}
      setEmail={setPrefilledEmail}
      title={title}
      showTrialFeatureList={isTrial}
    >
      <>
        {pathname.includes("appsumo") && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.appsumoNote}
          >
            {t("auth:appsumo.signup.note")}
          </Typography>
        )}
        {!isTrial ? (
          <SignUpView prefilledEmail={prefilledEmail} />
        ) : (
          <SignUpViewTrial />
        )}
      </>
    </CommonAuthContainer>
  );
};
